import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { UserLogin, userSignup } from "../../Service/userAuth";
import { emailRegex, passwpordRegex } from "../../Utils/Regex";
import { phoneRegex } from "../../Utils/constant";

interface GuestRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (userData: any) => void;
  serviceParams: any;
}

interface GuestFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  confirm_password: string;
}

type AuthMode = 'choice' | 'login' | 'signup';

const GuestRegistrationModal: React.FC<GuestRegistrationModalProps> = ({
  open,
  onClose,
  onSuccess,
  serviceParams
}) => {
  const [authMode, setAuthMode] = useState<AuthMode>('choice');
  const [formData, setFormData] = useState<GuestFormData>({
    first_name: serviceParams?.first_name || "",
    last_name: serviceParams?.last_name || "",
    email: serviceParams?.email || "",
    phone: serviceParams?.phone || "",
    password: "",
    confirm_password: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<GuestFormData>>({});

  // Update form data when serviceParams changes
  useEffect(() => {
    if (serviceParams) {
      setFormData(prev => ({
        ...prev,
        first_name: serviceParams.first_name || prev.first_name,
        last_name: serviceParams.last_name || prev.last_name,
        email: serviceParams.email || prev.email,
        phone: serviceParams.phone || prev.phone,
      }));
    }
  }, [serviceParams]);

  const validateForm = (): boolean => {
    const newErrors: Partial<GuestFormData> = {};

    // Email validation (required for both login and signup)
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation (required for both login and signup)
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (authMode === 'signup' && !passwpordRegex.test(formData.password)) {
      newErrors.password = "Password should contain 6 to 20 characters including one numeric digit, one uppercase letter, one lowercase letter and one special character";
    }

    // Signup-specific validations
    if (authMode === 'signup') {
      if (!formData.first_name.trim()) {
        newErrors.first_name = "First name is required";
      } else if (formData.first_name.length < 2) {
        newErrors.first_name = "First name must be at least 2 characters";
      }

      if (!formData.last_name.trim()) {
        newErrors.last_name = "Last name is required";
      } else if (formData.last_name.length < 2) {
        newErrors.last_name = "Last name must be at least 2 characters";
      }

      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number";
      }

      if (!formData.confirm_password) {
        newErrors.confirm_password = "Please confirm your password";
      } else if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof GuestFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (authMode === 'login') {
        // Handle login
        const loginData = {
          email: formData.email,
          password: formData.password
        };

        const response = await UserLogin(loginData);
        
        if (!response?.data?.success) {
          const msgArr = response?.data?.message;
          let msg = "";
          if (Array.isArray(msgArr)) {
            for (let i = 0; i < msgArr.length; i++) {
              msg += msgArr[i];
            }
          } else if (typeof msgArr === 'string') {
            msg = msgArr;
          } else {
            msg = "Login failed. Please try again.";
          }
          toast.error(msg);
          return;
        }

        // Login successful
        const userData = response?.data?.data;
        if (userData) {
          localStorage.setItem("Nurture_user_data", JSON.stringify(userData));
          localStorage.setItem("Nurture_user_token", userData.token);
        }
        
        toast.success("Login successful!");
        await onSuccess(userData);
        onClose();

      } else if (authMode === 'signup') {
        // Handle signup
        const registrationData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          password_confirmation: formData.confirm_password,
          type: 'customer',
          active: 1
        };

        const response = await userSignup(registrationData);
        
        if (!response?.data?.success) {
          const msgArr = response?.data?.message;
          let msg = "";
          if (Array.isArray(msgArr)) {
            for (let i = 0; i < msgArr.length; i++) {
              msg += msgArr[i];
            }
          } else if (typeof msgArr === 'string') {
            msg = msgArr;
          } else {
            msg = "Registration failed. Please try again.";
          }
          toast.error(msg);
          return;
        }

        // Signup successful
        const userData = response?.data?.data;
        if (userData) {
          localStorage.setItem("Nurture_user_data", JSON.stringify(userData));
          localStorage.setItem("Nurture_user_token", userData.token);
        }
        
        toast.success("Account created successfully!");
        await onSuccess(userData);
        onClose();
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      let errorMessage = "An error occurred during authentication";
      
      if (error.response?.data?.message) {
        const message = error.response.data.message;
        if (Array.isArray(message)) {
          // Handle array of validation errors
          errorMessage = message.join(' ');
        } else if (typeof message === 'string') {
          // Handle string error message
          errorMessage = message;
        }
      } else if (error.response?.data?.error) {
        // Handle error field
        errorMessage = error.response.data.error;
      } else if (error.message) {
        // Handle general error message
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form data when closing
      setFormData({
        first_name: serviceParams?.first_name || "",
        last_name: serviceParams?.last_name || "",
        email: serviceParams?.email || "",
        phone: serviceParams?.phone || "",
        password: "",
        confirm_password: ""
      });
      setErrors({});
      setAuthMode('choice');
      onClose();
    }
  };

  const handleModeChange = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setErrors({});
  };

  const handleBackToChoice = () => {
    setAuthMode('choice');
    setErrors({});
  };

  const renderChoiceScreen = () => (
    <div className={`modal fade ${open ? 'show' : ''}`} style={{ display: open ? 'block' : 'none' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content signup-poupmodal">
          <div className="modal-header border-0">
            <div className="text-center w-100">
              <h1 className="mt-4 pb-0 heading">
                Continue with Your <span style={{ color: "#4cc3b0" }}>Purchase</span>
              </h1>
              <p className="sign-up-popup-sub-title">
                Choose how you'd like to proceed
              </p>
            </div>
          </div>
          
          <div className="modal-body">
            <div className="text-center py-4">
              <div className="d-flex flex-column gap-3 align-items-center">
                <button
                  onClick={() => handleModeChange('login')}
                  disabled={isLoading}
                  className="btn btn-primary w-100"
                  style={{
                    backgroundColor: '#4cc3b0',
                    border: '2px solid #4cc3b0',
                    borderRadius: '25px',
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    maxWidth: '300px',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    boxShadow: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#3ba89a';
                    e.currentTarget.style.borderColor = '#3ba89a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#4cc3b0';
                    e.currentTarget.style.borderColor = '#4cc3b0';
                  }}
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleModeChange('signup')}
                  disabled={isLoading}
                  className="btn btn-outline-primary w-100"
                  style={{
                    border: '2px solid #4cc3b0',
                    color: '#4cc3b0',
                    borderRadius: '25px',
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    maxWidth: '300px',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4cc3b0';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#4cc3b0';
                  }}
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>
          
          <div className="modal-footer border-0">
            <button 
              onClick={handleClose} 
              disabled={isLoading}
              className="btn btn-secondary"
              style={{
                border: '2px solid #6c757d',
                borderRadius: '25px',
                padding: '8px 24px',
                transition: 'background-color 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5a6268';
                e.currentTarget.style.borderColor = '#5a6268';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6c757d';
                e.currentTarget.style.borderColor = '#6c757d';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAuthForm = () => (
    <div className={`modal fade ${open ? 'show' : ''}`} style={{ display: open ? 'block' : 'none' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content signup-poupmodal">
          <div className="modal-header">
            <div className="text-center w-100">
              <h1 className="mt-4 pb-0 heading">
                {authMode === 'login' ? 'Sign In' : 'Create'} <span style={{ color: "#4cc3b0" }}>{authMode === 'login' ? '' : 'Free Account'}</span>
              </h1>
              <p className="sign-up-popup-sub-title">
                To continue with your purchase
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="modal-body">
              <div className="sign-pop">
                <div className="container-fluid">
                  <div className="row">
                    {authMode === 'signup' && (
                      <>
                        <div className="col-sm-6 px-1">
                          <div className="form-group mb-3">
                            <label>First Name</label>
                            <input
                              type="text"
                              name="guest_first_name"
                              autoComplete="new-first-name"
                              className={`${errors.first_name && "error-input"} form-control`}
                              placeholder="Enter first name"
                              value={formData.first_name}
                              onChange={(e) => handleInputChange('first_name', e.target.value)}
                              disabled={isLoading}
                            />
                            <p className="error position-absolute mt-0">
                              {errors.first_name}
                            </p>
                          </div>
                        </div>
                        <div className="col-sm-6 px-1">
                          <div className="form-group mb-3">
                            <label>Last Name</label>
                            <input
                              type="text"
                              name="guest_last_name"
                              autoComplete="new-last-name"
                              className={`${errors.last_name && "error-input"} form-control`}
                              placeholder="Enter last name"
                              value={formData.last_name}
                              onChange={(e) => handleInputChange('last_name', e.target.value)}
                              disabled={isLoading}
                            />
                            <p className="error position-absolute mt-0">
                              {errors.last_name}
                            </p>
                          </div>
                        </div>
                        <div className="col-12 px-1">
                          <div className="form-group mb-3">
                            <label>Phone Number</label>
                            <input
                              type="tel"
                              name="guest_phone"
                              autoComplete="new-phone"
                              className={`${errors.phone && "error-input"} form-control`}
                              placeholder="Enter phone number"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              disabled={isLoading}
                            />
                            <p className="error position-absolute mt-0">
                              {errors.phone}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="col-12 px-1">
                      <div className="form-group mb-3">
                        <label>Email Address</label>
                        <input
                          type="email"
                          name="guest_email"
                          autoComplete="new-email"
                          className={`${errors.email && "error-input"} form-control`}
                          placeholder="Enter email address"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          disabled={isLoading}
                        />
                        <p className="error position-absolute mt-0">
                          {errors.email}
                        </p>
                      </div>
                    </div>
                    <div className="col-12 px-1">
                      <div className="form-group mb-3">
                        <label>Password</label>
                        <input
                          type="password"
                          name="password"
                          autoComplete="new-password"
                          className={`${errors.password && "error-input"} form-control`}
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          disabled={isLoading}
                        />
                        <p className="error position-absolute mt-0">
                          {errors.password}
                        </p>
                      </div>
                    </div>
                    {authMode === 'signup' && (
                      <div className="col-12 px-1">
                        <div className="form-group mb-3">
                          <label>Confirm Password</label>
                          <input
                            type="password"
                            name="confirm_password"
                            autoComplete="new-password"
                            className={`${errors.confirm_password && "error-input"} form-control`}
                            placeholder="Confirm password"
                            value={formData.confirm_password}
                            onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                            disabled={isLoading}
                          />
                          <p className="error position-absolute mt-0">
                            {errors.confirm_password}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={handleBackToChoice} 
                disabled={isLoading}
                className="btn btn-secondary"
                style={{ 
                  marginRight: '8px',
                  border: '2px solid #6c757d',
                  borderRadius: '25px',
                  padding: '8px 24px',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6268';
                  e.currentTarget.style.borderColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                  e.currentTarget.style.borderColor = '#6c757d';
                }}
              >
                Back
              </button>
              <button 
                onClick={handleClose} 
                disabled={isLoading}
                className="btn btn-secondary"
                style={{ 
                  marginRight: '8px',
                  border: '2px solid #6c757d',
                  borderRadius: '25px',
                  padding: '8px 24px',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6268';
                  e.currentTarget.style.borderColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                  e.currentTarget.style.borderColor = '#6c757d';
                }}
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  backgroundColor: '#4cc3b0',
                  border: '2px solid #4cc3b0',
                  borderRadius: '25px',
                  padding: '8px 24px',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#3ba89a';
                  e.currentTarget.style.borderColor = '#3ba89a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4cc3b0';
                  e.currentTarget.style.borderColor = '#4cc3b0';
                }}
              >
                {isLoading 
                  ? (authMode === 'login' ? "Signing In..." : "Creating Account...") 
                  : (authMode === 'login' ? "Sign In & Continue" : "Create Account & Continue")
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return authMode === 'choice' ? renderChoiceScreen() : renderAuthForm();
};

export default GuestRegistrationModal;
